import { AddMargins, Min, RemoveMargins, Scale, Subtract, Total } from "@pdfking/math"
import { ZERO_DISTANCE } from "@pdfking/math/constants"
import type { PageShape } from "@pdfking/renderer-api"
import { HorizontalAlign, PlotFunction, VerticalAlign } from "@pdfking/renderer-api"
import { FullMethodDispatcher } from "@pdfking/tfunc"

import { MONTH_LAYOUT, PALETTE } from "#constants"
import type { AgendaDesign, AgendaPageKind, CalendarArea, CalendarBoxes, DrawPage } from "#internal"

import { Area } from "../calendar/Area"
import { CalendarFrame } from "../calendar/CalendarFrame"
import { CalendarYearAreas } from "../calendar/CalendarYearAreas"
import { DrawMonthGrid } from "../calendar/DrawMonthGrid"
import { GenerateCalendarLayout } from "../calendar/GenerateCalendarLayout"
import { MonthName } from "../calendar/MonthName"
import { PictureCover } from "../calendar/PictureCover"
import { YearColumns } from "../calendar/YearColumns"
import { PageMetrics } from "../layout/PageMetrics"
import { Slice } from "../layout/Slice"
import { DrawNotepadPage } from "../notepad/DrawNotepadPage"
import { PlainText } from "../text/PlainText"
import { NotebookBackground } from "../types/NotebookBackground"
import { AgendaPagePlan } from "./AgendaPagePlan"
import { DrawAgendaContacts } from "./DrawAgendaContacts"
import { DrawAgendaInfo } from "./DrawAgendaInfo"
import { DrawAgendaWeek } from "./DrawAgendaWeek"
import { DrawAgendaWeekNotes } from "./DrawAgendaWeekNotes"

// A month's grid's share of the page's height
const GRID = 0.8

/** An agenda of `design`'s page, as `page` has it, drawn as its plan has it
 * (see AgendaPagePlan), its text within `security`, its paper running on
 * into `bleed`: the owner's details and contacts as the shop's templates
 * give them; a month a calendar's month grid; the next year a calendar's
 * year page; the title a calendar's cover, the pattern and the year; a
 * week's days under the week's header, or a day's; any other page a lined
 * page of notes, as a notepad's. Its months and weekdays named in
 * `language`, its fixed words `labels`. */
export const DrawAgendaPage =
  (design: AgendaDesign): DrawPage =>
  context => {
    const { bleed, labels, language, security, size, year } = context
    const page = { ...size, x: ZERO_DISTANCE, y: ZERO_DISTANCE }
    const frame = RemoveMargins(page, security)
    const { short } = PageMetrics(size)
    const pitch = Scale(short, 0.04)
    const top = Total(frame.y, frame.height)
    const paper = (shapes: PageShape[]): PageShape[] => [
      {
        enum: PlotFunction.PLOT_RECT,
        fill_color: PALETTE.paper,
        position: AddMargins(page, bleed),
      },
      ...shapes,
    ]
    // An agenda's notes ruled down to the frame's foot, no footer
    const notes = () =>
      DrawNotepadPage({ background: NotebookBackground.LINES, footer: false, page: context, pitch })
    // A calendar's month grid, its cells taller to write in, its numbers as
    // large as a calendar's
    const month_page = (month: number) => {
      const font = Scale(short, 0.06)
      const grid = Scale(frame.height, GRID)
      const numbers = Min(Scale(frame.height, 121 / 2800), Scale(frame.width, 0.06))
      return paper([
        PlainText(
          `${MonthName(month, language)} ${year}`.toUpperCase(),
          frame.x,
          frame.width,
          Subtract(top, font),
          font,
          PALETTE.primary
        ),
        ...DrawMonthGrid({
          box: { ...frame, height: grid, y: Subtract(top, Total(grid, Scale(font, 1.5))) },
          language,
          layout: {
            ...MONTH_LAYOUT,
            font: numbers,
            horizontal_align: HorizontalAlign.LEFT,
            vertical_align: VerticalAlign.TOP,
          },
          month,
          year,
        }),
      ])
    }
    // The calendars' pages: the next year's on the page before the title, a
    // dated agenda's, and the title their cover, the pattern and the year. An
    // agenda's inside pages have no footer, their areas down to the frame's
    // foot; the year on the pantone too
    const calendar = (areas: (boxes: CalendarBoxes) => CalendarArea[], of: number) =>
      GenerateCalendarLayout({
        areas: areas(CalendarFrame({ ...context, year: of }, false)),
        footer: false,
        page: context,
        spot_year: true,
      })
    const week = (week: number, days: number[], header: boolean, slots = days.length) =>
      paper(
        DrawAgendaWeek({
          days,
          design,
          frame,
          header,
          labels,
          language,
          pitch,
          slots,
          week,
          year,
        })
      )
    // A spread's pages four shares each one under the other, the ones its
    // days leave the week's notes; in columns its days alone
    const shares = (days: number[]) => (design.columns ? days.length : 4)
    return FullMethodDispatcher<[AgendaPageKind], PageShape[]>({
      contacts: () => paper(DrawAgendaContacts({ frame, labels })),
      day: ({ days, week: n }) => week(n, days, false),
      info: () => paper(DrawAgendaInfo({ frame, labels, pitch })),
      // Papyrus's spread headed by its right page's month alone, as both its
      // pages' days start level
      left: ({ days, week: n }) => week(n, days, !design.notes, shares(days)),
      month: ({ month }) => month_page(month),
      next_year: () => calendar(CalendarYearAreas, year + 1),
      notes,
      // The year planned, half a page, a column a month as a poster calendar's
      planner: ({ half }) =>
        calendar(({ body, gap, short }) => {
          const [head, rest] = Slice.top(body, Scale(short, 0.08), gap)
          return [
            Area.year(head, year),
            ...YearColumns({
              box: rest,
              first: 1 + 6 * half,
              gap,
              language,
              last: 6 + 6 * half,
              title: Scale(short, 0.035),
              year,
            }),
          ]
        }, year),
      // An undated spread's month and week on its every page
      right: ({ days, week: n }) => week(n, days, !design.dated, shares(days)),
      title: () => calendar(PictureCover({}), year),
      week: ({ week: n }) => week(n, [0, 1, 2, 3, 4, 5, 6], true),
      // Papyrus's right page its own
      week_notes: ({ week: n }) =>
        paper(DrawAgendaWeekNotes({ design, frame, language, pitch, week: n, year })),
    })(AgendaPagePlan(design, context))
  }
